import { PutEffect, all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import { TicketsRoutes, getPathForRoute } from "constants/tickets/TicketsRoutes";
import { ToastContent, getToastContent } from "constants/toasts";
import { EventPlace } from "helpers/analytics";
import { pushWithUTM } from "helpers/sagas";
import { SettingsRoutes } from "routes/settings/constants";
import { ApiManager } from "services/api";
import { Action, ISinglePayloadError } from "services/websocket/interfaces";
import { CRUDAction, RequestAction } from "store/entities/actions";
import {
  CANNED_RESPONSE,
  CannedResponseActions,
  IDeleteCannedResponseAttachmentPayload,
} from "store/entities/canned-responses";
import { CustomViewsActions } from "store/entities/custom-views/actions";
import { selectCustomViews } from "store/entities/custom-views/selectors";
import {
  EMAIL_DOMAIN,
  EmailDomainActions,
  EmailDomainActionsNames,
  ICreateEmailDomainSuccessPayload,
} from "store/entities/email-domains";
import { ILiveChatImportFinishedPayload, INTEGRATION, IntegrationsActions } from "store/entities/integrations";
import {
  IUpdateDefaultMailboxFailurePayload,
  IUpdateDefaultMailboxSuccessPayload,
  LICENSE,
  LicenseActions,
} from "store/entities/license";
import { MailboxesActions } from "store/entities/mailboxes/actions";
import { IMailbox } from "store/entities/mailboxes/interfaces";
import { TAG, TagActions } from "store/entities/tags";
import { TEAM, TeamActions } from "store/entities/teams";
import { EventTrackingActions } from "store/features/event-tracking/actions";
import { ToastAutoHideDelay, ToastVariant, ToastsActions } from "store/features/toasts";
import { IAction, IActionWithPayload } from "store/helper";
import { TicketsViewActions } from "store/views/tickets/actions";

import { SubscriptionViewActions } from "../subscription";

import { SettingsViewActions, SettingsViewActionsNames } from "./actions";
import {
  IAddCannedResponsePayload,
  IAddTagPayload,
  ICreateEmailDomainPayload,
  ICreateMailboxPayload,
  IDeleteCannedResponsePayload,
  IDeleteCustomViewPayload,
  IDeleteEmailDomainPayload,
  IDeleteMailboxPayload,
  IDeleteTagPayload,
  IDomainConfigurationCopiedPayload,
  IGoToCustomViewPayload,
  IUpdateCannedResponsePayload,
  IUpdateCompanyNamePayload,
  IUpdateCustomViewPayload,
  IUpdateDefaultMailboxPayload,
  IUpdateMailboxPayload,
  IUpdateNotificationsPayload,
  IUpdateTagPayload,
} from "./interfaces";

const SettingsRequestSource = "settings";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateCompanyName(action: IActionWithPayload<string, { companyName: string }>) {
  const { companyName } = action.payload;
  yield put(LicenseActions.setCompanyName({ companyName, requestSource: "settings" }));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* mailboxCopied() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.COPY_MAILBOX_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* domainConfigurationCopied(action: IActionWithPayload<string, IDomainConfigurationCopiedPayload>) {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.DOMAIN_CONFIGURATION_COPIED, action.payload),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* forwardingAddressCopied() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.FORWARDING_ADDRESS_COPIED),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* companyNameUpdated(action: IActionWithPayload<string, IUpdateCompanyNamePayload>) {
  const { requestSource } = action.payload;
  if (requestSource === "settings") {
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.COMPANY_NAME_UPDATED),
        variant: ToastVariant.Success,
      })
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* createEmailDomain(action: IActionWithPayload<string, ICreateEmailDomainPayload>) {
  yield put(EmailDomainActions.create({ value: action.payload, requestSource: SettingsRequestSource }));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* createEmailDomainSuccess(action: IActionWithPayload<string, ICreateEmailDomainSuccessPayload>) {
  const { requestSource } = action.payload;
  if (requestSource === SettingsRequestSource) {
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.EMAIL_DOMAIN_CREATE_SUCCESS),
        variant: ToastVariant.Success,
      })
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteEmailDomain(action: IActionWithPayload<string, IDeleteEmailDomainPayload>) {
  const { id } = action.payload;
  yield put(EmailDomainActions.delete({ id }));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteEmailDomainSuccess() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.EMAIL_DOMAIN_DELETE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* addTag(action: IActionWithPayload<string, IAddTagPayload>) {
  const { teamId, name } = action.payload;
  yield put(
    TagActions.create({
      value: { teamId, name },
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* addTagSuccess() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TAG_ADD_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* addTagFailure(action: IActionWithPayload<string, IAddTagPayload>) {
  const { type } = action.payload.error;
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TAG_ADD_FAILURE, { type }),
      variant: ToastVariant.Error,
      error: action?.payload?.error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateTag(action: IActionWithPayload<string, IUpdateTagPayload>) {
  const { id, name } = action.payload;
  yield put(
    TagActions.update({
      value: { id, name },
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateTagSuccess() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TAG_UPDATE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateTagFailure() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TAG_UPDATE_FAILURE),
      variant: ToastVariant.Error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteTag(action: IActionWithPayload<string, IDeleteTagPayload>) {
  const { id } = action.payload;
  yield put(TagActions.delete({ id }));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteTagSuccess() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TAG_DELETE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteTagFailure() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TAG_DELETE_FAILURE),
      variant: ToastVariant.Error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* addCannedResponse(action: IActionWithPayload<string, IAddCannedResponsePayload>) {
  const { teamId, richTextObj, shortcuts, transactionId } = action.payload;
  yield put(
    CannedResponseActions.create({
      value: { teamId, richTextObj, shortcuts, transactionId },
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* addCannedResponseSuccess() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.CANNED_RESPONSE_ADD_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* addCannedResponseFailure(action: IActionWithPayload<string, IAddCannedResponsePayload>) {
  const { type } = action.payload.error;
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.CANNED_RESPONSE_ADD_FAILURE, { type }),
      variant: ToastVariant.Error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateCannedResponse(action: IActionWithPayload<string, IUpdateCannedResponsePayload>) {
  const { id, richTextObj, shortcuts, transactionId } = action.payload;
  yield put(
    CannedResponseActions.update({
      value: { id, richTextObj, shortcuts, transactionId },
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateCannedResponseSuccess() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.CANNED_RESPONSE_UPDATE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateCannedResponseFailure() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.CANNED_RESPONSE_UPDATE_FAILURE),
      variant: ToastVariant.Error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteCannedResponseAttachment(action: IActionWithPayload<string, IDeleteCannedResponseAttachmentPayload>) {
  const { id, attachmentIndex } = action.payload;
  // @ts-expect-error TS(2345)
  yield put(CannedResponseActions.removeAttachment(id, attachmentIndex));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteCannedResponse(action: IActionWithPayload<string, IDeleteCannedResponsePayload>) {
  const { id } = action.payload;
  yield put(CannedResponseActions.delete({ id }));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteCannedResponseSuccess() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.CANNED_RESPONSE_DELETE_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteCannedResponseFailure() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.CANNED_RESPONSE_DELETE_FAILURE),
      variant: ToastVariant.Error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateNotifications(action: IActionWithPayload<string, IUpdateNotificationsPayload>) {
  const { teamId, settings } = action.payload;
  yield put(TeamActions.updateNotifications({ teamId, settings }));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateNotificationsSuccess() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TEAM_NOTIFICATIONS_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateNotificationsFailure() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.TEAM_NOTIFICATIONS_FAILURE),
      variant: ToastVariant.Error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* goToCustomView(action: IActionWithPayload<string, IGoToCustomViewPayload>) {
  const { id } = action.payload;
  yield put(pushWithUTM(getPathForRoute(TicketsRoutes.All)));
  yield put(TicketsViewActions.selectCustomView({ id }));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateCustomView(action: IActionWithPayload<string, IUpdateCustomViewPayload>) {
  const { id, name, shared } = action.payload;
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const { filters } = (yield select(selectCustomViews)).data[id];

  yield put(
    CustomViewsActions.update({
      value: {
        id,
        name,
        filters,
        shared,
      },
    })
  );

  yield put(EventTrackingActions.trackEvent("Custom views: Custom view updated", EventPlace.Settings, { shared }));
}
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteCustomView(action: IActionWithPayload<string, IDeleteCustomViewPayload>) {
  const { id } = action.payload;
  yield put(CustomViewsActions.delete({ id }));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* createMailbox(action: IActionWithPayload<string, ICreateMailboxPayload>) {
  yield put(
    MailboxesActions.create({
      value: action.payload,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateMailbox(action: IActionWithPayload<string, IUpdateMailboxPayload>) {
  yield put(
    MailboxesActions.update({
      value: action.payload,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteMailbox(action: IActionWithPayload<string, IDeleteMailboxPayload>) {
  yield put(MailboxesActions.delete(action.payload));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* deleteMailboxes(action: IActionWithPayload<string, IMailbox["id"][]>) {
  yield all(action.payload.map(id => put(MailboxesActions.delete({ id }))));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateDefaultMailbox(action: IActionWithPayload<string, IUpdateDefaultMailboxPayload>) {
  yield put(LicenseActions.updateDefaultMailbox({ ...action.payload, requestSource: SettingsRequestSource }));
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateDefaultMailboxFailure(action: IActionWithPayload<string, IUpdateDefaultMailboxFailurePayload>) {
  const { requestSource } = action.payload;

  if (requestSource === SettingsRequestSource) {
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.MAILBOX_UPDATE_FAILURE),
        variant: ToastVariant.Error,
      })
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* updateDefaultMailboxSuccess(action: IActionWithPayload<string, IUpdateDefaultMailboxSuccessPayload>) {
  const { requestSource } = action.payload;

  if (requestSource === SettingsRequestSource) {
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.MAILBOX_UPDATE_SUCCESS),
        variant: ToastVariant.Success,
      })
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchLiveChatImportFailure() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.IMPORT_LIVECHAT_DATA_START_FAILURE),
      variant: ToastVariant.Error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* startLiveChatImportSuccess() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.IMPORT_LIVECHAT_DATA_START_SUCCESS),
      variant: ToastVariant.Success,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* startLiveChatImportFailure() {
  yield put(
    ToastsActions.createToast({
      content: getToastContent(ToastContent.IMPORT_LIVECHAT_DATA_START_FAILURE),
      variant: ToastVariant.Error,
    })
  );
}

function* livechatSelectiveImportFinished(
  action: IActionWithPayload<string, { action: Action; error?: ISinglePayloadError }>
): Generator<PutEffect<IAction<string>>, void, unknown> {
  const { action: messageAction, error } = action.payload;
  yield put(IntegrationsActions.fetchLiveChatImportDiff());
  yield put(IntegrationsActions.fetchLiveChatSelectiveImport());

  if (error?.errorCode === "planLimitExceeded" && location.pathname === `${SettingsRoutes.Apps}/livechat`) {
    yield put(
      SubscriptionViewActions.openUpsellModal({
        source: "livechatImport",
      })
    );
  }

  if (messageAction === "done") {
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.IMPORT_LIVECHAT_DATA_FINISHED_SUCCESS),
        variant: ToastVariant.Success,
      })
    );
  }

  if (messageAction === "error") {
    yield put(
      ToastsActions.createToast({
        content: getToastContent(ToastContent.IMPORT_LIVECHAT_DATA_FINISHED_FAILURE, { error }),
        variant: ToastVariant.Error,
        autoHideDelayTime:
          error?.errorCode === "limitExceeded" || error?.errorCode === "paymentIssue"
            ? ToastAutoHideDelay.Infinite
            : ToastAutoHideDelay.Long,
      })
    );
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* liveChatImportFinishedSuccess(action: IActionWithPayload<string, ILiveChatImportFinishedPayload>) {
  const { success } = action.payload;
  yield put(
    ToastsActions.createToast({
      content: getToastContent(
        success
          ? ToastContent.IMPORT_LIVECHAT_DATA_FINISHED_SUCCESS
          : ToastContent.IMPORT_LIVECHAT_DATA_FINISHED_FAILURE
      ),
      variant: success ? ToastVariant.Success : ToastVariant.Error,
    })
  );
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function* fetchNextForwardingAddress() {
  // @ts-expect-error TS(7057): 'yield' expression implicitly results in an 'any' ... Remove this comment to see the full error message
  const response = yield call(ApiManager.mailboxesApi.fetchNextAddress);

  if (response.result) {
    yield put(SettingsViewActions.fetchNextForwardingAddressSuccess({ name: response.result.name }));
  } else {
    yield put(SettingsViewActions.fetchNextForwardingAddressFailure());
  }
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function* settingsViewSagas() {
  yield all([
    takeEvery(SettingsViewActionsNames.UPDATE_DEFAULT_MAILBOX_SETTINGS, updateDefaultMailbox),
    takeEvery(SettingsViewActionsNames.UPDATE_COMPANY_NAME, updateCompanyName),
    takeEvery(SettingsViewActionsNames.MAILBOX_COPIED, mailboxCopied),
    takeEvery(SettingsViewActionsNames.FORWARDING_ADDRESS_COPIED, forwardingAddressCopied),
    takeEvery(SettingsViewActionsNames.DOMAIN_CONFIGURATION_COPIED, domainConfigurationCopied),
    takeEvery(LICENSE.SET_COMPANY_NAME[RequestAction.SUCCESS], companyNameUpdated),
    takeEvery(SettingsViewActionsNames.CREATE_EMAIL_DOMAIN, createEmailDomain),
    takeLatest(EMAIL_DOMAIN[CRUDAction.CREATE][RequestAction.SUCCESS], createEmailDomainSuccess),
    takeEvery(SettingsViewActionsNames.DELETE_EMAIL_DOMAIN, deleteEmailDomain),
    takeLatest(EmailDomainActionsNames.SYNC_DELETE, deleteEmailDomainSuccess),

    takeEvery(SettingsViewActionsNames.ADD_TAG, addTag),
    takeLatest(TAG[CRUDAction.CREATE][RequestAction.SUCCESS], addTagSuccess),
    takeLatest(TAG[CRUDAction.CREATE][RequestAction.FAILURE], addTagFailure),

    takeEvery(SettingsViewActionsNames.UPDATE_TAG, updateTag),
    takeLatest(TAG[CRUDAction.UPDATE][RequestAction.SUCCESS], updateTagSuccess),
    takeLatest(TAG[CRUDAction.UPDATE][RequestAction.FAILURE], updateTagFailure),

    takeEvery(SettingsViewActionsNames.DELETE_TAG, deleteTag),
    takeLatest(TAG[CRUDAction.DELETE][RequestAction.SUCCESS], deleteTagSuccess),
    takeLatest(TAG[CRUDAction.DELETE][RequestAction.FAILURE], deleteTagFailure),

    takeLatest(LICENSE.UPDATE_DEFAULT_MAILBOX[RequestAction.SUCCESS], updateDefaultMailboxSuccess),
    takeLatest(LICENSE.UPDATE_DEFAULT_MAILBOX[RequestAction.FAILURE], updateDefaultMailboxFailure),

    takeEvery(SettingsViewActionsNames.ADD_CANNED_RESPONSE, addCannedResponse),
    takeLatest(CANNED_RESPONSE[CRUDAction.CREATE][RequestAction.SUCCESS], addCannedResponseSuccess),
    takeLatest(CANNED_RESPONSE[CRUDAction.CREATE][RequestAction.FAILURE], addCannedResponseFailure),

    takeEvery(SettingsViewActionsNames.UPDATE_CANNED_RESPONSE, updateCannedResponse),
    takeLatest(CANNED_RESPONSE[CRUDAction.UPDATE][RequestAction.SUCCESS], updateCannedResponseSuccess),
    takeLatest(CANNED_RESPONSE[CRUDAction.UPDATE][RequestAction.FAILURE], updateCannedResponseFailure),

    takeEvery(SettingsViewActionsNames.DELETE_CANNED_RESPONSE, deleteCannedResponse),
    takeLatest(CANNED_RESPONSE[CRUDAction.DELETE][RequestAction.SUCCESS], deleteCannedResponseSuccess),
    takeLatest(CANNED_RESPONSE[CRUDAction.DELETE][RequestAction.FAILURE], deleteCannedResponseFailure),

    takeEvery(SettingsViewActionsNames.DELETE_CANNED_RESPONSE_ATTACHMENT, deleteCannedResponseAttachment),

    takeEvery(SettingsViewActionsNames.UPDATE_NOTIFICATIONS[RequestAction.REQUEST], updateNotifications),
    takeLatest(TEAM.UPDATE_NOTIFICATIONS[RequestAction.SUCCESS], updateNotificationsSuccess),
    takeLatest(TEAM.UPDATE_NOTIFICATIONS[RequestAction.FAILURE], updateNotificationsFailure),

    takeEvery(SettingsViewActionsNames.GO_TO_CUSTOM_VIEW, goToCustomView),
    takeEvery(SettingsViewActionsNames.UPDATE_CUSTOM_VIEW, updateCustomView),
    takeEvery(SettingsViewActionsNames.DELETE_CUSTOM_VIEW, deleteCustomView),

    takeEvery(SettingsViewActionsNames.CREATE_MAILBOX, createMailbox),
    takeEvery(SettingsViewActionsNames.UPDATE_MAILBOX, updateMailbox),
    takeEvery(SettingsViewActionsNames.DELETE_MAILBOX, deleteMailbox),
    takeEvery(SettingsViewActionsNames.DELETE_MAILBOXES, deleteMailboxes),

    takeEvery(INTEGRATION.LIVECHAT_IMPORT_FETCH[RequestAction.FAILURE], fetchLiveChatImportFailure),
    takeEvery(INTEGRATION.LIVECHAT_IMPORT_START[RequestAction.SUCCESS], startLiveChatImportSuccess),
    takeEvery(INTEGRATION.LIVECHAT_IMPORT_START[RequestAction.FAILURE], startLiveChatImportFailure),
    takeEvery(INTEGRATION.LIVECHAT_IMPORT_FINISHED, liveChatImportFinishedSuccess),

    takeEvery(INTEGRATION.LIVECHAT_SELECTIVE_IMPORT_FINISHED, livechatSelectiveImportFinished),

    takeEvery(
      SettingsViewActionsNames.FETCH_NEXT_FORWARDING_ADDRESS[RequestAction.REQUEST],
      fetchNextForwardingAddress
    ),
  ]);
}
